import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "./useAppLoading";

interface IBadge {
  _id?: string;
  name?: string;
  description?: string;
  icon?: string;
  minDrops?: number;
  maxDrops?: number;
  type?: string;
  colors?: string[];
  createdAt?: string;
  updatedAt?: string;
}

interface IStore {
  /** State */
  badges: IBadge[];
  oneBadge: IBadge | null;
  createdBadge: string | null;
  updatedBadge: string | null;
  deletedBadge: string | null;
  /** Callbacks */
  getBadges: () => void;
  getOneBadge: (id: string) => void;
  createBadge: (badge: IBadge) => void;
  updateBadge: (id: string, badge: IBadge) => void;
  deleteBadge: (id: string) => void;
  clearCreatedBadge: () => void;
  clearUpdatedBadge: () => void;
  clearDeletedBadge: () => void;
}

export const useBadges = create<IStore>()((set) => ({
  /** State */
  badges: [],
  oneBadge: null,
  createdBadge: null,
  updatedBadge: null,
  deletedBadge: null,
  /** Callbacks */
  getBadges: async () => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get("/badges");
      if (response?.data) {
        set({ badges: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching badges", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getOneBadge: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`/badges/${id}`);
      if (response?.data) {
        set({ oneBadge: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching one badge", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  createBadge: async (badge: IBadge) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("/badges", badge);
      if (response?.data) {
        set({ createdBadge: response.data?._id });
      }
    } catch (error) {
      LOG.error("Error on creating badge", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateBadge: async (id: string, badge: IBadge) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`/badges/${id}`, badge);
      if (response?.data) {
        set({ updatedBadge: response.data?._id });
      }
    } catch (error) {
      LOG.error("Error on updating badge", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteBadge: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(`/badges/${id}`);
      if (response?.data) {
        set((state) => ({
          badges: state.badges.filter(
            (badge) => badge._id !== response.data?._id
          ),
          deletedBadge: response.data?._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on deleting badge", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearCreatedBadge: () => {
    set({ createdBadge: null });
  },
  clearUpdatedBadge: () => {
    set({ updatedBadge: null });
  },
  clearDeletedBadge: () => {
    set({ deletedBadge: null });
  },
}));
